import { GatewayProviderCodes } from "./gateway";
import type { IBrandGateway } from "../../models/gateways";

/**
 * @name isDLocalGateway
 * @desc Returns true if the brand gateway is the dLocal redirect gateway, which
 * requires a payer document ID to be sent in `payment_method_addition`
 */
export function isDLocalGateway(brandGateway?: IBrandGateway): boolean {
  const gateway = brandGateway?.gateway;
  return (
    gateway?.gateway_provider?.code === GatewayProviderCodes.D_LOCAL ||
    gateway?.provider === GatewayProviderCodes.D_LOCAL
  );
}

/**
 * Per-country regex patterns for dLocal document validation.
 * Countries not listed here have no format constraint — only the `required`
 * check applies.
 * @see https://docs.dlocal.com/reference/country-reference
 */
export const DOCUMENT_REGEX_RULES: Record<string, RegExp> = {
  AE: /^\d{15}$/, // Emirates ID: exactly 15 digits
  AR: /^(\d{7,9}|\d{11})$/, // DNI: 7–9 digits | CUIT/CUIL: 11 digits
  BH: /^\d{9}$/, // Smart Card / CPR: 9 digits
  BO: /^\d{5,20}$/, // CI: 5–20 digits
  BR: /^(\d{11}|\d{14})$/, // CPF: 11 digits | CNPJ: 14 digits
  CL: /^\d{8,9}$/, // CI/RUT: 8–9 digits
  CN: /^(\d{18}|\d{17}[Xx])$/, // Citizen ID: 18 digits or 17+X
  CO: /^\d{6,11}$/, // CC/NIT: 6–11 digits
  CR: /^\d{9}$/, // CI: exactly 9 digits
  EC: /^\d{5,20}$/, // CI/RUC: 5–20 digits
  EG: /^\d{14}$/, // National ID: 14 digits
  GH: /^[A-Z]{3}\d{10}$/, // Ghana Card: 3 letters + 10 digits
  GT: /^\d{13}$/, // CUI: 13 digits
  HN: /^\d{13}$/, // DNI: 13 digits
  ID: /^\d{16}$/, // NIK: 16 digits
  IN: /^[A-Z]{5}\d{4}[A-Z]$/, // PAN: 5 letters + 4 digits + 1 letter
  JO: /^\d{10}$/, // National ID: 10 digits
  JP: /^\d{12}$/, // My Number: 12 digits
  KE: /^\d{6,9}$/, // National ID / Maisha ID: 6–9 digits
  MG: /^\d{12}$/, // National ID: 12 digits
  MX: /^[A-Z0-9]{10,18}$/, // CURP: 10–18 chars | RFC: 12–13 chars
  MY: /^\d{12}$/, // NRIC: 12 digits
  NE: /^\d{7}$/, // CNI: 7 digits
  NG: /^\d{11}$/, // NIN: 11 digits
  NI: /^\d{13}[A-Z]$/, // DNI: 13 digits + 1 letter
  OM: /^\d{9}$/, // National ID: 9 digits
  PE: /^(\d{8,9}|\d{11})$/, // DNI: 8–9 digits | RUC: 11 digits
  PH: /^\d{12}$/, // PSN: 12 digits
  PK: /^\d{13}$/, // CNIC: 13 digits
  PY: /^\d{5,20}$/, // CI/RUC: 5–20 digits
  RW: /^\d{16}$/, // National ID: 16 digits
  SA: /^\d{10}$/, // National ID: 10 digits
  SN: /^\d{13,17}$/, // CNI / ECOWAS: 13–17 digits
  SV: /^\d{9}$/, // DUI: 9 digits
  TH: /^\d{13}$/, // Thai ID: 13 digits
  TZ: /^\d{20}$/, // National ID: 20 digits
  TD: /^\d{13}$/, // CNI: 13 digits
  UG: /^\d{14,17}$/, // NIC: 14–17 digits
  UY: /^(\d{6,8}|\d{12})$/, // CI: 6–8 digits | RUT: 12 digits
  VN: /^(\d{9}|\d{13})$/, // VNID: 9 or 13 digits
  ZA: /^\d{13}$/ // SA Identity Card: 13 digits
};

/**
 * Returns the country-specific document name for a given country+currency
 * combination, per dLocal's country reference. These are region-specific
 * abbreviations that must not be translated.
 * @see https://docs.dlocal.com/reference/country-reference#general-information
 */
export function getDocumentName(
  country: string | undefined,
  currency: string | undefined
): string {
  if (!country || !currency) return "Document";

  const key = `${country.toUpperCase()}_${currency.toUpperCase()}`;

  switch (key) {
    case "AR_ARS":
      return "DNI / CUIT / CUIL";
    case "BH_BHD":
      return "Smart Card / CPR";
    case "BD_BDT":
      return "NID Card";
    case "BO_BOB":
      return "CI / NIT";
    case "BR_BRL":
      return "CPF / CNPJ";
    case "CM_XAF":
      return "CNI / ID";
    case "TD_XAF":
      return "CNI";
    case "CL_CLP":
      return "CI / RUT";
    case "CN_CNY":
      return "Citizen ID Number";
    case "CO_COP":
      return "CC / NIT";
    case "CR_CRC":
      return "CI";
    case "CD_CDF":
      return "National ID Card";
    case "DO_DOP":
      return "ID";
    case "EC_USD":
      return "CI";
    case "SV_USD":
      return "DUI";
    case "EG_EGP":
      return "ID";
    case "GH_GHS":
      return "Ghana Card";
    case "GT_GTQ":
      return "CUI";
    case "GW_XOF":
      return "CEDEAO National ID Card";
    case "HN_HNL":
      return "DNI";
    case "IN_INR":
      return "PAN";
    case "ID_IDR":
      return "NIK";
    case "CI_XOF":
      return "CNI";
    case "JP_JPY":
      return "My Number";
    case "JO_JOD":
      return "National ID Card";
    case "KE_KES":
      return "National ID Card";
    case "MG_MGA":
      return "National ID Card";
    case "MY_MYR":
      return "NRIC";
    case "MX_MXN":
      return "CURP / RFC";
    case "MA_MAD":
      return "CNIE";
    case "NI_NIO":
      return "DNI";
    case "NE_XOF":
      return "CNI";
    case "NG_NGN":
      return "NIN";
    case "OM_OMR":
      return "National ID Card";
    case "PK_PKR":
      return "CNIC";
    case "PA_USD":
      return "Cedula de Identidad";
    case "PY_PYG":
      return "CI";
    case "PE_PEN":
      return "DNI / RUC";
    case "PH_PHP":
      return "PSN";
    case "RW_RWF":
      return "National ID Card";
    case "SA_SAR":
      return "National ID Card";
    case "SN_XOF":
      return "CNI / ECOWAS ID";
    case "ZA_ZAR":
      return "SA Identity Card";
    case "LK_LKR":
      return "National ID Card";
    case "TZ_TZS":
      return "National ID Card";
    case "TH_THB":
      return "Thai Identity Card";
    case "TR_TRY":
      return "T.C. Kimlik No.";
    case "UG_UGX":
      return "NIC";
    case "AE_AED":
      return "Emirates ID";
    case "UY_UYU":
      return "CI / RUT";
    case "VN_VND":
      return "VNID";
    case "ZM_ZMW":
      return "National Registration Card";
    case "ZW_ZWL":
      return "National ID Card";
    default:
      return "Document";
  }
}
